import { z } from 'zod';
import { StatesSchema } from '@duke-hq/svc-api-db/schemas/enums/States.schema';
import { EmployerContactMethodSchema } from '@duke-hq/svc-api-db/schemas/enums/EmployerContactMethod.schema';
import { EmployerContactLogTypeSchema } from '@duke-hq/svc-api-db/schemas/enums/EmployerContactLogType.schema';
import { EmployerContactLogOutcomeSchema } from '@duke-hq/svc-api-db/schemas/enums/EmployerContactLogOutcome.schema';
import { DrnSchema } from '@duke-hq/drn/schema';
import { isValidAbn, normaliseAbn } from '@duke-hq/shared-constants/employer';
import { phoneRegex } from '@duke-hq/shared-constants/regexPatterns';

export const NEW_CONTACT_VALUE = '__new__';

// A blank ABN is stored as null, not '' — a missing ABN is absent, not the empty
// string. Whitespace is always stripped. REF: (https://abr.business.gov.au/Help/AbnFormat)
const NormalisedAbnSchema = z
  .string()
  .nullable()
  .transform((value) => {
    const stripped = normaliseAbn(value ?? '');
    return stripped === '' ? null : stripped;
  });

export const AbnSchema = NormalisedAbnSchema.refine(
  (abn) => abn == null || isValidAbn(abn),
  'Enter a valid 11-digit ABN',
);

export const EmployerContactInputSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  email: z.email().nullable(),
  phone: z.string().nullable(),
  preferredContactMethod: EmployerContactMethodSchema.nullable(),
});

export function refineContactMethod(
  values: { email: string | null; phone: string | null; preferredContactMethod: string | null },
  ctx: z.RefinementCtx,
): void {
  if (values.preferredContactMethod === 'EMAIL' && (values.email == null || values.email === '')) {
    ctx.addIssue({
      code: 'custom',
      path: ['email'],
      message: 'Add an email address for the preferred contact method',
    });
  }
  if (values.preferredContactMethod === 'PHONE' && (values.phone == null || values.phone === '')) {
    ctx.addIssue({
      code: 'custom',
      path: ['phone'],
      message: 'Add a phone number for the preferred contact method',
    });
  }
}

export function refineContactFormFields(
  values: { email: string; phone: string; preferredContactMethod: '' | 'EMAIL' | 'PHONE' },
  ctx: z.RefinementCtx,
): void {
  if (values.email !== '' && !z.email().safeParse(values.email).success) {
    ctx.addIssue({ code: 'custom', path: ['email'], message: 'Enter a valid email address' });
  }
  if (values.phone !== '' && !phoneRegex.test(values.phone)) {
    ctx.addIssue({
      code: 'custom',
      path: ['phone'],
      message: 'Enter a valid Australian phone number',
    });
  }
  refineContactMethod(values, ctx);
}

export const EmployerContactCreateSchema = z
  .object({
    employerId: z.string(),
    isPrimary: z.boolean().default(false),
  })
  .extend(EmployerContactInputSchema.shape)
  .superRefine(refineContactMethod);

export const EmployerContactUpdateSchema = z
  .object({
    contactId: z.string(),
    isPrimary: z.boolean().optional(),
  })
  .extend(EmployerContactInputSchema.shape)
  .superRefine(refineContactMethod);

export const EmployerContactDeleteSchema = z.object({ contactId: z.string() });

export const EmployerNoteCreateSchema = z.object({
  employerId: z.string(),
  text: z.string().trim().min(1, 'Note text is required').max(10_000),
});

export const EmployerContactLogContactSchema = z.discriminatedUnion('mode', [
  z.object({ mode: z.literal('none') }),
  z.object({ mode: z.literal('existing'), contactId: z.string().min(1) }),
  z.object({
    mode: z.literal('new'),
    firstName: z.string().trim().min(1, 'First name is required'),
    lastName: z.string().trim().min(1, 'Last name is required'),
    email: z
      .string()
      .trim()
      .nullable()
      .refine((value) => value == null || z.email().safeParse(value).success, {
        message: 'Enter a valid email address',
      }),
    phone: z
      .string()
      .trim()
      .nullable()
      .refine((value) => value == null || phoneRegex.test(value), {
        message: 'Enter a valid Australian phone number',
      }),
    addToContacts: z.boolean(),
  }),
]);

const EmployerContactLogFieldsSchema = z.object({
  type: EmployerContactLogTypeSchema,
  // A wall-clock datetime-local value ("2026-08-24T09:00") carries no offset, so
  // it's paired with the admin's IANA timezone and resolved to a UTC instant on
  // write. Storing the bare string as a Date would read it as UTC and shift it.
  occurredAt: z.string().min(1),
  timezone: z.string().min(1),
  result: EmployerContactLogOutcomeSchema.nullable(),
  notes: z.string().trim().max(2000).nullable(),
  contact: EmployerContactLogContactSchema,
});

export const EmployerContactLogListSchema = z.object({ employerId: z.string() });

export const EmployerContactLogCreateSchema = z
  .object({ employerId: z.string(), nominationId: z.cuid().optional() })
  .extend(EmployerContactLogFieldsSchema.shape);

export const EmployerContactLogUpdateSchema = z
  .object({ logId: z.string(), nominationId: z.cuid().optional() })
  .extend(EmployerContactLogFieldsSchema.shape);

export const EmployerContactLogCreateForNominationSchema = z
  .object({ nominationId: z.cuid() })
  .extend(EmployerContactLogFieldsSchema.shape);

export const EmployerContactLogFormSchema = z
  .object({
    type: EmployerContactLogTypeSchema,
    occurredAt: z.string().min(1, 'Enter when the contact happened'),
    result: z.union([EmployerContactLogOutcomeSchema, z.literal('')]),
    contactSelection: z.string(),
    newFirstName: z.string().trim(),
    newLastName: z.string().trim(),
    newEmail: z.string().trim(),
    newPhone: z.string().trim(),
    addToContacts: z.boolean(),
    notes: z.string().trim(),
  })
  .superRefine((values, ctx) => {
    if (values.contactSelection !== NEW_CONTACT_VALUE) {
      return;
    }
    if (values.newFirstName === '') {
      ctx.addIssue({ code: 'custom', path: ['newFirstName'], message: 'First name is required' });
    }
    if (values.newLastName === '') {
      ctx.addIssue({ code: 'custom', path: ['newLastName'], message: 'Last name is required' });
    }
    if (values.newEmail !== '' && !z.email().safeParse(values.newEmail).success) {
      ctx.addIssue({ code: 'custom', path: ['newEmail'], message: 'Enter a valid email address' });
    }
    if (values.newPhone !== '' && !phoneRegex.test(values.newPhone)) {
      ctx.addIssue({
        code: 'custom',
        path: ['newPhone'],
        message: 'Enter a valid Australian phone number',
      });
    }
  });

export const CreateEmployerSchema = z.object({
  name: z.string().min(1, 'Business name is required'),
  abn: AbnSchema,
  businessType: z.string().max(200).nullable(),
  formattedAddress: z.string().nullable(),
  addressSubpremise: z.string().nullable(),
  addressStreetNumber: z.string().nullable(),
  addressStreetName: z.string().nullable(),
  addressLocality: z.string().nullable(),
  addressState: StatesSchema.nullable(),
  addressPostcode: z.string().nullable(),
  contact: EmployerContactInputSchema.optional().nullable(),
  placementCapacity: z.number().int().nonnegative().nullable(),
  agreementFileDrns: z.array(DrnSchema).default([]),
  agreementExpiresAt: z.coerce.date().nullable().default(null),
  insuranceFileDrns: z.array(DrnSchema).default([]),
  insuranceExpiresAt: z.coerce.date().nullable().default(null),
  courseIds: z.array(z.string()).max(200).default([]),
});

export const UpdateEmployerDetailsSchema = z
  .object({ employerId: z.string() })
  .extend(CreateEmployerSchema.pick({ name: true, businessType: true }).shape)
  .extend({ abn: NormalisedAbnSchema });

export const SaveNominationToDirectorySchema = CreateEmployerSchema.extend({
  nominationId: z.cuid(),
});

export const employerBusinessFieldsShape = {
  name: z.string().trim().min(1, 'Business name is required'),
  abn: z
    .string()
    .trim()
    .refine(
      (value) => value === '' || isValidAbn(normaliseAbn(value)),
      'Enter a valid 11-digit ABN',
    ),
  businessType: z.string().trim(),
  businessTypeOther: z.string().trim(),
};

export function refineBusinessType(
  values: { businessType: string; businessTypeOther: string },
  ctx: z.RefinementCtx,
): void {
  if (values.businessType === 'Other' && values.businessTypeOther.trim() === '') {
    ctx.addIssue({
      code: 'custom',
      path: ['businessTypeOther'],
      message: 'Enter the business type',
    });
  }
}

export const EmployerBusinessFieldsSchema = z
  .object(employerBusinessFieldsShape)
  .superRefine(refineBusinessType);

export const EmployerContactFieldsSchema = z
  .object({
    firstName: z.string().trim(),
    lastName: z.string().trim(),
    email: z.string().trim(),
    phone: z.string().trim(),
    preferredContactMethod: z.enum(['', 'EMAIL', 'PHONE']),
  })
  .superRefine((contact, ctx) => {
    const anyFilled = [contact.firstName, contact.lastName, contact.email, contact.phone].some(
      (value) => value !== '',
    );
    if (!anyFilled) {
      return;
    }
    if (contact.firstName === '') {
      ctx.addIssue({ code: 'custom', path: ['firstName'], message: 'First name is required' });
    }
    if (contact.lastName === '') {
      ctx.addIssue({ code: 'custom', path: ['lastName'], message: 'Last name is required' });
    }
    refineContactFormFields(contact, ctx);
  });

export const EmployerBusinessDetailsSchema = z
  .object({
    ...employerBusinessFieldsShape,
    contact: EmployerContactFieldsSchema,
  })
  .superRefine(refineBusinessType);

export const EmployerContactFormSchema = z
  .object({
    firstName: z.string().trim().min(1, 'First name is required'),
    lastName: z.string().trim().min(1, 'Last name is required'),
    email: z.string().trim(),
    phone: z.string().trim(),
    preferredContactMethod: z.enum(['', 'EMAIL', 'PHONE']),
    isPrimary: z.boolean(),
  })
  .superRefine(refineContactFormFields);

export type CreateEmployerInput = z.input<typeof CreateEmployerSchema>;
export type CreateEmployerPayload = z.infer<typeof CreateEmployerSchema>;
export type UpdateEmployerDetailsPayload = z.infer<typeof UpdateEmployerDetailsSchema>;
export type EmployerDetailsWritePayload = Omit<UpdateEmployerDetailsPayload, 'employerId'>;
export type SaveNominationToDirectoryPayload = z.infer<typeof SaveNominationToDirectorySchema>;
export type EmployerBusinessFieldValues = z.infer<typeof EmployerBusinessFieldsSchema>;
export type EmployerBusinessDetailsValues = z.infer<typeof EmployerBusinessDetailsSchema>;
export type EmployerContactCreatePayload = z.infer<typeof EmployerContactCreateSchema>;
export type EmployerContactUpdatePayload = z.infer<typeof EmployerContactUpdateSchema>;
export type EmployerContactFormValues = z.infer<typeof EmployerContactFormSchema>;
export type EmployerContactWritePayload = Omit<EmployerContactCreatePayload, 'employerId'>;
export type EmployerContactInput = z.infer<typeof EmployerContactInputSchema>;
export type EmployerContactLogContactInput = z.infer<typeof EmployerContactLogContactSchema>;
export type EmployerContactLogCreatePayload = z.infer<typeof EmployerContactLogCreateSchema>;
export type EmployerContactLogUpdatePayload = z.infer<typeof EmployerContactLogUpdateSchema>;
export type EmployerContactLogFormValues = z.infer<typeof EmployerContactLogFormSchema>;
export type EmployerContactLogWritePayload = Omit<EmployerContactLogCreatePayload, 'employerId'>;
export type EmployerContactLogCreateForNominationPayload = z.infer<
  typeof EmployerContactLogCreateForNominationSchema
>;
